import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Params, Router } from '@angular/router';

import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, map, shareReplay, switchMap, takeUntil, tap } from 'rxjs/operators';

import { isEqual } from 'lodash-es';

import { BaseContainer, MetabaseReport, RequestPaginationInfo } from '@stylehaven/components';

import { DEFAULT_PAGE_SIZE } from '../../../helpers/constants';
import { HIERARCHY_TABLE_SCROLL_REF } from '../../../record/components/hierarchical-list/hierarchical-list.component';
import { HierarchyRow, toRecordRow } from '../../../record/entities/hierarchy-row.entity';
import { PaginatedResults } from '../../../record/entities/pagination/paginated-results.entity';
import { PaginationInfo } from '../../../record/entities/pagination/pagination-info.entity';
import { Column } from '../../../record/entities/column.entity';
import { ReportRow } from '../../entities/report-row.entity';
import { toReportColumns } from '../../helpers/report-columns.helper';
import { ReportsSandboxService } from '../../sandbox/reports-sandbox.service';

@Component({
    standalone: false,
    selector: 'digihaven-reports-overview-container',
    templateUrl: './reports-overview-container.component.html',
    host: { class: 'dgh-overview-page' }
})
export class ReportsOverviewContainerComponent extends BaseContainer implements OnInit, OnDestroy {
    request$!: Observable<RequestPaginationInfo>;
    pagedResult$!: Observable<PaginatedResults<MetabaseReport>>;

    rows$!: Observable<HierarchyRow<ReportRow>[]>;
    columns$!: Observable<Column<ReportRow>[]>;
    paginationInfo$!: Observable<PaginationInfo>;

    tableHeaderWithCount$!: Observable<string>;

    readonly paginationScrollRef: string = HIERARCHY_TABLE_SCROLL_REF;

    constructor(
        private readonly sandbox: ReportsSandboxService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly router: Router
    ) {
        super();
    }

    ngOnInit(): void {
        this.initSourceStreams();

        this.initTableHeaderStream();
        this.initColumns();
    }

    paginationInfoChanged(paginationInfo: RequestPaginationInfo): void {
        this.router.navigate([], {
            relativeTo: this.activatedRoute,
            queryParams: {
                ...this.activatedRoute.snapshot.queryParams,
                page: paginationInfo.page,
                size: paginationInfo.resultsPerPage
            }
        });
    }

    private initColumns(): void {
        this.columns$ = of(toReportColumns(this.translateService));
    }

    private initTableHeaderStream(): void {
        this.tableHeaderWithCount$ = this.paginationInfo$.pipe(
            map(paginationInfo =>
                this.translateService.instant('reports.table.results', {
                    items: paginationInfo.totalNumberOfResults
                })
            )
        );
    }

    private initSourceStreams(): void {
        this.request$ = this.activatedRoute.queryParams.pipe(
            map(queryParams => this.mapParamsToRequest(queryParams)),
            distinctUntilChanged(isEqual),
            takeUntil(this.destroy$)
        );

        this.pagedResult$ = this.request$.pipe(
            tap(() => this.startLoading()),
            switchMap(request => this.sandbox.getReports(request).pipe(catchError(() => this.onGetReportsError()))),
            tap(() => this.stopLoading()),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.rows$ = this.pagedResult$.pipe(
            map(pagedResult => this.toRows(pagedResult.results ?? [])),
            shareReplay({ bufferSize: 1, refCount: true })
        );

        this.paginationInfo$ = this.pagedResult$.pipe(
            map(pagedResult => pagedResult.paginationInfo),
            shareReplay({ bufferSize: 1, refCount: true })
        );
    }

    private toRows(reports: MetabaseReport[]): HierarchyRow<ReportRow>[] {
        return reports.map(report => {
            const row: ReportRow = new ReportRow(report);

            return toRecordRow<ReportRow>(row.key, of(row), false);
        });
    }

    private mapParamsToRequest(params: Params | null): RequestPaginationInfo {
        const { page, size } = params || {};

        return {
            page: (page && size && page * size) || 0,
            resultsPerPage: Number(size) || DEFAULT_PAGE_SIZE
        };
    }

    private startLoading(): void {
        this.setError(false);
        this.setLoading(true);
    }

    private stopLoading(): void {
        this.setError(false);
        this.setLoading(false);
    }

    private onGetReportsError(): Observable<never> {
        this.setLoading(false);
        this.setError(true);

        return EMPTY;
    }
}
